/** @jsx jsx */

import { css, jsx } from '@emotion/core';
import { addScormResponseAction } from 'actions/addScormResponseAction';
import { updateScoreAction } from 'actions/updateScoreAction';
import {
  cloneElement,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { useScoringQuizzes } from 'services/apiHelpers/useScoringQuizzes';
import pubSub from 'services/pubSub';
import { unlockInteraction } from 'services/unlockInteraction';
import { interactionContext } from './interactionContext';

import { HostingContext } from 'hosting/context';
import { hotspotQuizEvent } from 'hosting/events';

interface IHotSpotScorableContext {
  selected: any[];
  updateSelected: (item: any) => number[] | void;
  attempt: number;
  numberOfAttempts: number;
  updateAttempt: () => void;
  useMediaInFeedback: boolean;
  showCorrectAnswer: boolean;
  submitState: 'initial' | 'pending' | 'resolved';
  correctAnswers: number[];
}

const HotSpotScorableContext = createContext({});

enum SubmitState {
  INITIAL = 'initial',
  PENDING = 'pending',
  RESOLVED = 'resolved',
}

const HotSpotScorable = props => {
  const { children } = props;

  const [hotspot, feedbacks] = children;
  const [
    correctFeedback,
    incorrectFeedback,
    attemptFeedback,
    submitBtn,
  ] = feedbacks.props.children;

  const hotspots = hotspot.props.children;
  const InteractionContext = useContext(interactionContext);
  const hostingCtx = useContext(HostingContext);

  const { id: InteractionContextId } = InteractionContext;

  // need to base this scorable on the immediateFeedback bool
  // only place score becomes relevant is on submission. the action obj
  const {
    lockDown,
    immediateFeedback = true,
    score,
  } = InteractionContext?.props;

  const {
    correctAnswers = [],
    showAttemptNumber = true,
    useMediaInFeedback = true,
    numberOfAttempts = 1,
    showCorrectAnswer = true,
    navigationBehaviour = {
      correct: false,
      incorrect: false,
    },
  } = props;

  const [result, setResult] = useState<string | null>(null);
  const [selected, setSelected] = useState<any[]>([]);
  const [attempt, setAttempt] = useState<number>(0);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [submitState, setSubmitState] = useState<SubmitState>(
    SubmitState.INITIAL
  );

  const scoringQuizzes = useScoringQuizzes();
  const dispatch = useDispatch();

  const updateSelected = (item): number[] => {
    if (correctAnswers.length === 1) {
      setSelected([item]);
      return [item];
    }

    const updatedSelected = [...selected];

    if (updatedSelected.includes(item)) {
      updatedSelected.splice(updatedSelected.indexOf(item), 1);
    } else {
      updatedSelected.push(item);
    }

    setSelected(updatedSelected);

    return updatedSelected;
  };

  const updateAttempt = () => {
    setAttempt(attempt => {
      return (attempt += 1);
    });

    if (attempt === numberOfAttempts - 1) {
      setSubmitState(SubmitState.RESOLVED); // 'resolved'
      setResult('incorrect');
      setFeedback('incorrect');
      return;
    }

    setResult('attempt');
    setFeedback('attempt');
  };

  const onSubmit = () => {
    const isCorrect =
      selected.sort().join('') === correctAnswers.sort().join('');

    if (isCorrect) {
      setSubmitState(SubmitState.RESOLVED); // 'resolved'
      setResult('correct');
      //
      setFeedback('correct');
      return;
    }

    updateAttempt();
  };

  useEffect(() => {
    //

    const InteractionContextId = InteractionContext.id;
    const isCorrect = result === 'correct';

    let questionText: HTMLDivElement | string = document.querySelector(
      `#i${InteractionContextId} .chameleon-text`
    ) as HTMLDivElement;

    if (questionText) {
      questionText = questionText.innerText;
    }

    if (isCorrect || attempt >= numberOfAttempts) {
      // score &&
      const responseExpanded = selected
        .map(entry => {
          return hotspots[entry].props.name;
        })
        .join(',');

      const correctExpanded = correctAnswers
        .map(entry => {
          return hotspots[entry].props.name;
        })
        .join(',');

      const action = {
        id: InteractionContextId,
        type: 'fill-in',
        question: questionText,
        reportable: 'multichoice' as 'multichoice',
        response: responseExpanded,
        correct: correctExpanded,
        result: (isCorrect ? 1 : 0) as 0 | 1,
      };

      hostingCtx?.data(
        hotspotQuizEvent(InteractionContextId, {
          values: selected,
          responseIds: selected,
          type: correctAnswers.length > 1 ? 'multi' : 'single',
          success: isCorrect,
          attempt_number: attempt,
        })
      );

      // dispatch if there's no scoringQuizzes etc
      let shouldDispatchScore = score;

      if (scoringQuizzes.length) {
        shouldDispatchScore = false;
      }

      if (shouldDispatchScore) {
        dispatch(updateScoreAction(InteractionContextId, isCorrect ? 1 : 0));
      }

      dispatch(addScormResponseAction(action));
    }
  }, [feedback]);

  useEffect(() => {
    const pS = pubSub.subscribe(
      `SHOW_HOTSPOT_FEEDBACK_${InteractionContext.id}`,
      $ => {
        setResult($.result);
        setFeedback($.result);
      }
    );

    return pS;
  }, []);

  useEffect(() => {
    if (submitState === SubmitState.RESOLVED) {
      // 'resolved'
      unlockInteraction(InteractionContextId);
    }
  }, [submitState]);

  const value: IHotSpotScorableContext = {
    selected,
    updateSelected,
    attempt,
    numberOfAttempts,
    updateAttempt,
    useMediaInFeedback,
    submitState,
    showCorrectAnswer,
    correctAnswers,
  };

  if (immediateFeedback) {
    return hotspot;
  }

  return (
    <HotSpotScorableContext.Provider value={value}>
      {hotspot}

      {feedback &&
        cloneElement(feedbacks, {
          result: result,
          attempt,
          navigationBehaviour,
          showAttemptNumber,
          numberOfAttempts,
          useMediaInFeedback,
          onOpened: () => {},

          onClose: () => {
            setResult(null);
            setFeedback(null);
          },
        })}

      <div
        css={css({
          display: 'flex',
          justifyContent: 'center',
          margin: '20px 0 0 0',
          opacity: selected.length ? 1 : 0,
          ...(window.dev() && {
            opacity: 1,
          }),
          ...(!window.dev() &&
            submitState === 'resolved' && {
              opacity: 0.2,
              pointerEvents: 'none',
            }),

          transition: 'opacity 0.2s',
        })}
      >
        {cloneElement(submitBtn, {
          onClick: onSubmit,
        })}
      </div>
    </HotSpotScorableContext.Provider>
  );
};

export default HotSpotScorable;
export { HotSpotScorableContext, IHotSpotScorableContext };
